// What was said on a call (BL-083), for owner and managers (AD-9, recordings.play): on the call's
// card, and one tap away in the customer's history. Calls are kept as text only, so the card shows
// the transcript, turn by turn with its times, or "Transcript on its way" while Twilio's is being
// made; Play is there only for audio kept from before, and its link lasts a few minutes. Below it,
// the recordings that fit more than one mirrored call, to say which.
import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { usePaged } from '../blueprint/paged.ts';
import { shownText } from '../shared/pages.ts';
import { phoneText } from '../types.ts';
import {
  lengthText,
  MATCH_INPUT,
  matchRows,
  noWords,
  sourceWord,
  turns,
  type CallRecording,
} from './transcript-model.ts';
import './transcript.css';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Couldn’t load it. Try again.');

/**
 * The call's recording and transcript; nothing when it has none. `known` is calls.get's (stripped for
 * those without recordings.play), so the card doesn't ask twice; `compact` waits for a tap. Keyed by
 * the call where it's shown, so another call starts afresh.
 */
export function CallTranscript({
  callId,
  known,
  compact = false,
}: {
  callId: string;
  known?: CallRecording | null;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(!compact);
  const [rec, setRec] = useState<CallRecording | null | undefined>(known);
  const [error, setError] = useState<string | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || rec !== undefined) return;
    let live = true;
    client
      .call<CallRecording | null>('calls.transcript', { callId })
      .then((r) => live && setRec(r))
      .catch((e: unknown) => live && (setRec(null), setError(say(e))));
    return () => {
      live = false;
    };
  }, [open, rec, callId]);
  const play = async () => {
    setBusy(true);
    setError(null);
    try {
      setSrc((await client.call<{ url: string }>('calls.playRecording', { callId })).url);
    } catch (e) {
      setError(e instanceof RpcError && e.code === 'not_found' ? 'No longer kept.' : say(e));
    } finally {
      setBusy(false);
    }
  };
  if (compact && !open)
    return (
      <button className="btn quiet sm" aria-expanded="false" onClick={() => setOpen(true)}>
        What was said
      </button>
    );
  if (rec === null && !error) return null; // no recording of this call
  return (
    <div className="call-said" aria-busy={rec === undefined}>
      {!compact && <h3 className="h5">What was said</h3>}
      {rec && (
        <p className="muted small">
          {lengthText(rec.durationSeconds)}
          {rec.transcript && ` · ${sourceWord(rec.transcript.source)}`}
        </p>
      )}
      {rec?.transcript ? (
        <ul className="call-said-turns">
          {turns(rec.transcript.segments).map((t, i) => (
            <li key={i} className="call-said-turn">
              {(t.speaker || t.at) && (
                <b className="call-said-who small">
                  {t.speaker}
                  {t.speaker && t.at && ' · '}
                  {t.at && <time>{t.at}</time>}
                </b>
              )}
              {t.text}
            </li>
          ))}
        </ul>
      ) : (
        rec && (
          <p className="ink2 small" role={rec.pending ? 'status' : undefined}>
            {noWords(rec)}
          </p>
        )
      )}
      {rec?.audio && !src && (
        <button className="btn quiet sm" disabled={busy} onClick={() => void play()}>
          {busy ? 'Getting it…' : 'Play the recording'}
        </button>
      )}
      {src && (
        <audio
          controls
          autoPlay
          src={src}
          aria-label="The call recording"
          onError={() => setError('It couldn’t be played. Try again.')}
        />
      )}
      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Twilio recordings that fit more than one mirrored call: an owner or manager says which. */
export function RecordingsToMatch({
  whenOf,
  toast,
}: {
  whenOf: (iso: string) => string;
  toast: (words: string) => void;
}) {
  // a page at a time, "Load more" reading on (list caps, BL-110); the server keeps only those that fit
  // more than one call
  const list = usePaged('calls.recordingsToMatch', MATCH_INPUT, matchRows);
  const rows = list.rows;
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pick = async (recordingId: string, callId: string) => {
    setBusy(recordingId);
    setError(null);
    try {
      await client.call('calls.linkRecording', { recordingId, callId });
      toast('Recording linked to the call');
      list.reload();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(null);
    }
  };
  const fits = (rows ?? []).filter((r) => r.candidates.length > 1);
  if (!fits.length) return null;
  return (
    <section className="stone card call-match">
      <h2 className="h4">Which call is this recording?</h2>
      <p className="ink2 small">More than one call fits. Pick the one it is.</p>
      <ul className="call-match-rows">
        {fits.map((r) => (
          <li key={r.recordingId} className="call-match-row">
            <span>
              <b>{phoneText(r.from)}</b> · {whenOf(r.startedAt)} · {lengthText(r.durationSeconds)}
            </span>
            {r.candidates.map((c) => (
              <button
                key={c.callId}
                className="btn soft wide"
                disabled={busy !== null}
                onClick={() => void pick(r.recordingId, c.callId)}
              >
                {whenOf(c.startedAt)} · {c.customerName ?? 'Unknown caller'}
              </button>
            ))}
          </li>
        ))}
      </ul>
      {(list.more || list.busy) && (
        <p className="call-match-more">
          <span className="muted small">{shownText(rows!.length, list.first?.total ?? rows!.length)}</span>
          <button className="btn soft" disabled={list.busy} onClick={() => list.more?.()}>
            {list.busy ? 'Loading…' : 'Load more'}
          </button>
        </p>
      )}
      {(error ?? (rows && list.error)) && (
        <p className="error small" role="alert">
          {error ?? list.error}
        </p>
      )}
    </section>
  );
}
